import { ApolloError } from "@apollo/client";
import { Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { GaiaHookFormRootErrorAlert } from "~/modules/app/components/GaiaHookFormRootErrorAlert";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import SpeciesSelectElement from "~/shared/components/SpeciesSelectElement";

import { SettingsResearchTypeSpeciesCreateInput } from "../../index";

interface AddSpeciesDialogProps {
    error: ApolloError | undefined;
    researchTypeId: string;
    onClose: () => void;
    onSubmitClick: (input: SettingsResearchTypeSpeciesCreateInput) => void;
    visible: boolean;
}

export function AddSpeciesDialog({ error, researchTypeId, visible, onClose, onSubmitClick }: AddSpeciesDialogProps) {
    const { t } = useTranslation("researchType");

    const {
        handleSubmit,
        setError,
        control,
        formState: { errors },
    } = useForm<SettingsResearchTypeSpeciesCreateInput>({
        defaultValues: {
            speciesId: "",
            showToClientUser: false,
        },
    });

    useApplyErrorToForm(error, setError);

    return (
        <Dialog open={visible} onClose={onClose}>
            <DialogContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {t("researchTypeSpeciesListCard.actionAddSpecies")}
                </Typography>
                <form onSubmit={handleSubmit(onSubmitClick)}>
                    <Stack gap={2} sx={{ width: "400px" }}>
                        <GaiaHookFormRootErrorAlert control={control} />

                        <Controller
                            control={control}
                            name="speciesId"
                            render={({ field }) => (
                                <SpeciesSelectElement
                                    label={t("researchTypeSpeciesForm.labelAddSpecies")}
                                    value={field.value}
                                    onChange={field.onChange}
                                    excludeAssignedToResearchTypeId={researchTypeId}
                                    error={!!errors.speciesId}
                                    helperText={errors.speciesId?.message}
                                />
                            )}
                        />

                        <SwitchElement
                            control={control}
                            label={t("researchTypeSpeciesForm.labelShowToClientUser")}
                            name="showToClientUser"
                        />

                        <GaiaHookFormSubmitButton control={control}>
                            {t("researchTypeSpeciesForm.btnSubmit")}
                        </GaiaHookFormSubmitButton>
                    </Stack>
                </form>
            </DialogContent>
        </Dialog>
    );
}
